import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { ChevronRight, MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Breadcrumb Component
 *
 * A navigation component that shows the current page's location within a site hierarchy.
 *
 * @example
 * ```tsx
 * <Breadcrumb>
 *   <BreadcrumbList>
 *     <BreadcrumbItem>
 *       <BreadcrumbLink href="/">Home</BreadcrumbLink>
 *     </BreadcrumbItem>
 *     <BreadcrumbSeparator />
 *     <BreadcrumbItem>
 *       <BreadcrumbPage>Current Page</BreadcrumbPage>
 *     </BreadcrumbItem>
 *   </BreadcrumbList>
 * </Breadcrumb>
 * ```
 */
function Breadcrumb({ ...props }: React.ComponentProps<"nav">) {
    return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}
Breadcrumb.displayName = "Breadcrumb";

/**
 * BreadcrumbList Component
 *
 * Container for breadcrumb items.
 */
function BreadcrumbList({ className, ...props }: React.ComponentProps<"ol">) {
    return (
        <ol
            data-slot="breadcrumb-list"
            className={cn(
                "setu:text-muted-foreground setu:flex setu:flex-wrap setu:items-center setu:gap-1.5 setu:text-sm setu:break-words setu:sm:gap-2.5",
                className
            )}
            {...props}
        />
    );
}
BreadcrumbList.displayName = "BreadcrumbList";

/**
 * BreadcrumbItem Component
 *
 * Individual breadcrumb item container.
 */
function BreadcrumbItem({ className, ...props }: React.ComponentProps<"li">) {
    return (
        <li
            data-slot="breadcrumb-item"
            className={cn("setu:inline-flex setu:items-center setu:gap-1.5", className)}
            {...props}
        />
    );
}
BreadcrumbItem.displayName = "BreadcrumbItem";

/**
 * BreadcrumbLink Component
 *
 * Clickable breadcrumb link.
 */
function BreadcrumbLink({
    asChild,
    className,
    ...props
}: React.ComponentProps<"a"> & {
    asChild?: boolean;
}) {
    const Comp = asChild ? Slot : "a";

    return (
        <Comp
            data-slot="breadcrumb-link"
            className={cn("setu:hover:text-foreground setu:transition-colors", className)}
            {...props}
        />
    );
}
BreadcrumbLink.displayName = "BreadcrumbLink";

/**
 * BreadcrumbPage Component
 *
 * Current page indicator (non-clickable).
 */
function BreadcrumbPage({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="breadcrumb-page"
            role="link"
            aria-disabled="true"
            aria-current="page"
            className={cn("setu:text-foreground setu:font-normal", className)}
            {...props}
        />
    );
}
BreadcrumbPage.displayName = "BreadcrumbPage";

/**
 * BreadcrumbSeparator Component
 *
 * Separator between breadcrumb items.
 */
function BreadcrumbSeparator({ children, className, ...props }: React.ComponentProps<"li">) {
    return (
        <li
            data-slot="breadcrumb-separator"
            role="presentation"
            aria-hidden="true"
            className={cn("setu:[&>svg]:size-3.5", className)}
            {...props}
        >
            {children ?? <ChevronRight />}
        </li>
    );
}
BreadcrumbSeparator.displayName = "BreadcrumbSeparator";

/**
 * BreadcrumbEllipsis Component
 *
 * Ellipsis indicator for truncated breadcrumbs.
 */
function BreadcrumbEllipsis({ className, ...props }: React.ComponentProps<"span">) {
    return (
        <span
            data-slot="breadcrumb-ellipsis"
            role="presentation"
            aria-hidden="true"
            className={cn("setu:flex setu:size-9 setu:items-center setu:justify-center", className)}
            {...props}
        >
            <MoreHorizontal className="setu:size-4" />
            <span className="setu:sr-only">More</span>
        </span>
    );
}
BreadcrumbEllipsis.displayName = "BreadcrumbEllipsis";

export {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbEllipsis,
};
